import { useState, useEffect } from "react";
import { useLanguage } from "@/lib/LanguageContext";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const PHONE = "(781) 475-6126";
const PHONE_LINK = "tel:+17814756126";

const languages = [
  { code: "en", label: "EN" },
  { code: "pt", label: "PT" },
  { code: "es", label: "ES" },
];

const navSections = ["home", "services", "about", "area", "reviews", "contact"];

export const Header = () => {
  const { t, lang, changeLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileOpen(false);
    }
  };

  const handleLangChange = (code) => {
    changeLanguage(code);
    window.history.replaceState(null, "", `/${code}`);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass-dark border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            data-testid="header-logo"
            onClick={() => scrollToSection("home")}
            className="flex items-center gap-2.5 hover:opacity-90 transition-opacity duration-300"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_boston-auto-service/artifacts/cfvpdtrh_WhatsApp%20Image%202026-05-11%20at%201.21.44%20PM.jpeg"
              alt="SousA Logo"
              className="w-9 h-9 md:w-10 md:h-10 object-contain rounded-full"
            />
            <span className="font-heading text-xl md:text-2xl font-bold tracking-wider text-white">
              SOUSA
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navSections.map((section) => (
              <button
                key={section}
                data-testid={`nav-${section}`}
                onClick={() => scrollToSection(section)}
                className="text-xs tracking-[0.2em] uppercase text-slate-300 hover:text-gold transition-colors duration-300 font-body font-semibold"
              >
                {t.nav[section]}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="language-switcher"
                className="flex items-center gap-1 text-xs tracking-widest uppercase text-slate-300 hover:text-white transition-colors font-body font-semibold px-3 py-2 border border-white/10 hover:border-white/20"
              >
                {lang?.toUpperCase() || "EN"}
                <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-navy-dark border-white/10 min-w-0">
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-switch-${l.code}`}
                    onClick={() => handleLangChange(l.code)}
                    className="text-xs tracking-widest uppercase text-slate-300 hover:text-gold cursor-pointer font-body"
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Call CTA */}
            <a
              href={PHONE_LINK}
              data-testid="header-call-btn"
              className="hidden md:flex items-center gap-2 bg-gold text-black font-heading text-xs tracking-wider uppercase px-5 py-2.5 hover:bg-gold-hover transition-all duration-300 animate-pulse-glow font-bold"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={2} />
              {t.nav.callNow}
            </a>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger
                data-testid="mobile-menu-btn"
                className="lg:hidden p-2 text-white"
              >
                {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
              </SheetTrigger>
              <SheetContent side="right" className="bg-navy-dark border-white/10 w-72">
                <SheetTitle className="flex items-center gap-2.5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_boston-auto-service/artifacts/cfvpdtrh_WhatsApp%20Image%202026-05-11%20at%201.21.44%20PM.jpeg"
                    alt="SousA Logo"
                    className="w-8 h-8 object-contain rounded-full"
                  />
                  <span className="font-heading text-lg tracking-wider text-white">
                    SOUSA
                  </span>
                </SheetTitle>
                <nav className="flex flex-col gap-1">
                  {navSections.map((section) => (
                    <button
                      key={section}
                      data-testid={`mobile-nav-${section}`}
                      onClick={() => scrollToSection(section)}
                      className="text-left text-sm tracking-[0.15em] uppercase text-slate-300 hover:text-gold hover:bg-white/5 transition-all duration-300 font-body font-semibold px-4 py-3"
                    >
                      {t.nav[section]}
                    </button>
                  ))}
                </nav>
                <a
                  href={PHONE_LINK}
                  className="mt-8 flex items-center justify-center gap-2 bg-gold text-black font-heading text-sm tracking-wider uppercase px-5 py-3 hover:bg-gold-hover transition-all duration-300 font-bold"
                >
                  <Phone className="w-4 h-4" strokeWidth={2} />
                  {PHONE}
                </a>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
